<script setup lang="ts">
import TheTopBar from '@/components/layout/TheTopBar.vue'
import TheSidebar from '@/components/layout/TheSidebar.vue'
import HiddenDevMenu from '@/components/modals/HiddenDevMenu.vue'
import MitreTechniqueModal from '@/components/modals/MitreTechniqueModal.vue'
import { sidebarItemFor } from '@/router/sidebarNav'
import useLayoutStore from '@/stores/layout.store'
import { ChevronRightIcon } from '@lucide/vue'
import { useElementSize } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { type RouteLocationMatched, useRoute } from 'vue-router'

const route = useRoute()
const routes = computed(() => {
  const i = route.matched.findIndex((r) => r.meta?.finalTopLevelBreadcrumb)
  return route.matched.slice(0, i === -1 ? undefined : i + 1)
})
// Segments that hide their title (e.g. Assets tabs) are skipped, so the
// final breadcrumb is the last visible one.
const visibleRoutes = computed(() =>
  routes.value.filter((rt) => !rt.meta.hideTitle),
)
// A page whose tabs all hide their titles (Account) still shows its parent's.
const showHeader = computed(
  () =>
    visibleRoutes.value.some((rt) => rt.meta.title) || !!route.meta.infoCard,
)

const headerIcon = (rt: RouteLocationMatched) =>
  sidebarItemFor(rt.name)?.icon ?? rt.meta.icon

const isFinalBreadcrumb = (rt: RouteLocationMatched) => {
  return rt?.name === visibleRoutes.value[visibleRoutes.value.length - 1]?.name
}

const mounted = ref(false)
onMounted(() => (mounted.value = true))

// Widens the page past the centered container only while the view's content
// doesn't fit it. `centeredEl` is an empty copy of the centered container, kept
// in place in both modes, so the width the content would get back is always
// measurable — even while the page is full width and the real container isn't
// centered.
const { contentOverflow } = storeToRefs(useLayoutStore())
const centeredEl = ref<HTMLElement | null>(null)
const contentEl = ref<HTMLElement | null>(null)
const { width: centeredWidth } = useElementSize(centeredEl)
const { width: contentWidth } = useElementSize(contentEl)
const isFullWidth = computed(() => {
  if (route.meta.fullWidth) return true
  // Only for a view that is asking, and only once both widths are known: they
  // arrive in separate callbacks, and comparing a measured width against a
  // not-yet-measured 0 sent the page wide, where it then measured wide and
  // stayed.
  if (contentOverflow.value === null) return false
  if (!centeredWidth.value || !contentWidth.value) return false
  // The 1px absorbs sub-pixel rounding, which would otherwise flip the layout
  // on a table that fits exactly.
  return contentWidth.value + contentOverflow.value > centeredWidth.value + 1
})
</script>

<template>
  <div id="top-div" class="min-h-screen bg-background">
    <div v-if="mounted" class="flex h-screen">
      <TheSidebar />

      <div class="flex flex-1 flex-col overflow-hidden">
        <!-- Fixed height navbar container -->
        <div class="h-[62px] shrink-0">
          <TheTopBar />
        </div>

        <main class="flex-1 overflow-y-auto md:scrollbar-stable">
          <div id="pre-container" />
          <div
            ref="centeredEl"
            aria-hidden="true"
            class="container mx-auto h-0 px-4"
          />
          <div
            ref="contentEl"
            :class="[
              isFullWidth ? 'w-full' : 'container mx-auto',
              'bg-background px-4',
            ]"
          >
            <div
              v-if="showHeader"
              class="flex min-h-[68px] items-center gap-3 px-3 py-3"
            >
              <!-- Earlier segments link back; the page's own name doesn't. -->
              <h1 class="flex items-center gap-2 text-2xl">
                <template v-for="(rt, idx) in visibleRoutes" :key="rt.name">
                  <component
                    :is="headerIcon(rt)"
                    v-if="headerIcon(rt)"
                    :size="24"
                    :class="[
                      'shrink-0',
                      { 'text-muted-foreground': !isFinalBreadcrumb(rt) },
                    ]"
                  />
                  <span
                    v-if="isFinalBreadcrumb(rt)"
                    id="title"
                    class="flex items-center gap-x-2"
                  >
                    <span
                      v-if="!rt.meta?.hasDynamicTitle"
                      class="font-extrabold [text-box:trim-both_cap_alphabetic]"
                    >
                      {{ rt.meta!.title }}
                    </span>
                  </span>
                  <RouterLink
                    v-else
                    :to="{ name: rt.name }"
                    class="font-extrabold text-muted-foreground [text-box:trim-both_cap_alphabetic] hover:text-default-font-color"
                  >
                    {{ rt.meta!.title }}
                  </RouterLink>
                  <ChevronRightIcon
                    v-if="idx < visibleRoutes.length - 1"
                    class="shrink-0 text-muted-foreground"
                    :size="18"
                  />
                </template>
              </h1>
              <!-- flex reaches the Dialog's wrapper div, whose 24px line box otherwise holds the icon 4px high. -->
              <component :is="route.meta.infoCard" v-if="route.meta.infoCard" class="flex" />
            </div>
            <slot />
          </div>
          <div id="post-container" />
        </main>
      </div>
    </div>
  </div>
  <Teleport to="body">
    <MitreTechniqueModal />
    <HiddenDevMenu />
  </Teleport>
</template>

<style scoped></style>
